import React, { useState } from "react";
import styled from "styled-components";
import {
    ChartData,
    DailyUserMetricObj,
    DailyUserMetricsMap,
} from "@lifeat/shared/data-models/user/UserMetrics/UserMetricsEntity";
import * as DateUtil from "@lifeat/shared/util/DateUtil";
import LinkButton from "@common/LifeAtButton/Variants/LinkButton";
import LifeAtDropdownSelector from "@common/LifeAtDropdownSelector/LifeAtDropdownSelector";
import LifeAtDropdownSelectorElement from "@common/LifeAtDropdownSelector/LifeAtDropdownSelectorElement";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import UserMetricChart from "./UserMetricChart";
import TEASER_CHART_IMAGE_SRC_DARK from "/resources/images/metricsmodalpremiumpicdark.png";
import TEASER_CHART_IMAGE_SRC_LIGHT from "/resources/images/metricsmodalpremiumpiclight.png";

const MS_TO_HRS = 3600000;

const TEASER_CHART_BUTTON_TEXT = "Upgrade to view Focus Time Tracking";
const TIME_SPENT_CHART_TITLE = "Time spent on app";
const FOCUS_TIME_CHART_TITLE = "Pomodoro time";
const DATE_RANGE_DROPDOWN: { [key: string]: string } = {
    "7": "last 7 days",
    "14": "last 14 days",
};

const DateRangeDropdown = styled(LifeAtDropdownSelector)`
    align-self: flex-start;
    .dropdown-selector-footer {
        display: none;
    }
`;

const DateRangeDropdownElement = styled.div`
    background-color: ${(props) => props.theme.backgroundPrimary};
    width: 100%;
    padding: 8px 10px;
`;

const TeaserChartImg = styled.img`
    width: 100%;
`;

const TeaserChartWrapper = styled.div`
    width: 100%;
    position: relative;
`;

const UpgradeButton = styled(LinkButton)`
    position: absolute;
    top: 45%;
    width: 100%;
    text-align: center;
`;

interface UserMetricDetailedViewProps {
    readonly data: DailyUserMetricsMap;
    readonly maxDays: number;
    readonly onClose: () => void;
}

const formatDataForChart = (
    obj: DailyUserMetricsMap,
    maxDays: number,
    metricKey: keyof DailyUserMetricObj,
): ChartData => {
    let dates: string[] = [];
    let times: number[] = [];

    const today = new Date();
    today.setDate(today.getDate() - (maxDays - 1));

    for (let i = 0; i < maxDays; i++) {
        const dateFormatted = DateUtil.formatDateStringYYYYMMDD(today);
        dates.push(dateFormatted);
        times.push(obj[dateFormatted] && obj[dateFormatted][metricKey] ? obj[dateFormatted][metricKey] / MS_TO_HRS : 0);
        today.setDate(today.getDate() + 1);
    }

    return { x: dates, y: times };
};

export default function UserMetricDetailedView(props: UserMetricDetailedViewProps) {
    const { userData }: UserContextType = useUserContext();
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();

    const [dateRange, setDateRange] = useState<string>(Object.keys(DATE_RANGE_DROPDOWN)[0]);

    const dateRangeDropDown = (
        <DateRangeDropdown
            className={`user-metrics-date-dropdown`}
            selectedElementId={dateRange}
            selectedElementName={DATE_RANGE_DROPDOWN[dateRange]}
        >
            {Object.keys(DATE_RANGE_DROPDOWN).map((key) => {
                return (
                    <LifeAtDropdownSelectorElement key={key}>
                        <DateRangeDropdownElement onClick={() => setDateRange(key)}>
                            {DATE_RANGE_DROPDOWN[key]}
                        </DateRangeDropdownElement>
                    </LifeAtDropdownSelectorElement>
                );
            })}
        </DateRangeDropdown>
    );

    const handleUpgradeClick = () => {
        setUpgradeCtaContext("productivityReports", UpgradeCTASource.FOCUS_TIME_METRIC);
        props.onClose();
    };

    const alternateContent = (
        <TeaserChartWrapper className={`teaser-chart-wrapper`}>
            <TeaserChartImg src={lightMode ? TEASER_CHART_IMAGE_SRC_LIGHT : TEASER_CHART_IMAGE_SRC_DARK} />
            <UpgradeButton
                label={TEASER_CHART_BUTTON_TEXT}
                handleClick={handleUpgradeClick}
                className={`user-metric-modal-upgrade-button`}
            />
        </TeaserChartWrapper>
    );

    return (
        <>
            {dateRangeDropDown}
            <UserMetricChart
                title={TIME_SPENT_CHART_TITLE}
                numDays={parseInt(dateRange)}
                data={formatDataForChart(props.data, props.maxDays, "timeSpent")}
            />
            <UserMetricChart
                title={FOCUS_TIME_CHART_TITLE}
                numDays={parseInt(dateRange)}
                data={formatDataForChart(props.data, props.maxDays, "focusTime")}
                hide={!userData?.proUserInfo?.isProUser}
                alternateContent={alternateContent}
            />
        </>
    );
}
